import {
  HdBodygraphCenter,
  HdBodygraphChartPreset,
  HdBodygraphConfig,
  HdBodygraphPlanetaryActivation,
  HdBodygraphRenderState,
  HdBodygraphSide,
} from '../hd-bodygraph.models';
import { HD_BODYGRAPH_GATE_TO_CENTER } from '../hd-bodygraph-metadata';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import { HdFixingState, HdPlanetKey } from './hd-planet-display-config';

export type HdArrowDirection = 'left' | 'right';

/**
 * Determines the arrow direction for a Variable arrow.
 * Tones 1–3 = left, tones 4–6 = right.
 */
export function resolveHdArrowDirection(tone: number): HdArrowDirection {
  return tone <= 3 ? 'left' : 'right';
}

/**
 * Resolves the 4 Variable arrows from the planetary activations.
 * Returns null if the preset has no planetary activations.
 */
export function resolveHdVariableArrows(
  preset: HdBodygraphChartPreset,
): { design: [HdArrowDirection, HdArrowDirection]; personality: [HdArrowDirection, HdArrowDirection] } | null {
  const designSun = findActivation(preset, 'design', 0);
  const designNode = findActivation(preset, 'design', 3);
  const personalitySun = findActivation(preset, 'personality', 0);
  const personalityNode = findActivation(preset, 'personality', 3);

  if (!designSun || !designNode || !personalitySun || !personalityNode) {
    return null;
  }

  return {
    design: [resolveHdArrowDirection(designSun.tone), resolveHdArrowDirection(designNode.tone)],
    personality: [resolveHdArrowDirection(personalitySun.tone), resolveHdArrowDirection(personalityNode.tone)],
  };
}

/**
 * Determines whether a gate sits in a defined center.
 */
export function isGateInDefinedCenter(gate: number, renderState: HdBodygraphRenderState): boolean {
  const center: HdBodygraphCenter | undefined = HD_BODYGRAPH_GATE_TO_CENTER[gate];

  if (!center) {
    return false;
  }

  return renderState.centerStates[center]?.isDefined ?? false;
}

/**
 * Bepaalt of een poort deel uitmaakt van een volledig defined kanaal.
 */
export function isGateInDefinedChannel(gate: number, renderState: HdBodygraphRenderState): boolean {
  for (const channelKey of Object.keys(renderState.channelStates)) {
    const channel = renderState.channelStates[channelKey];
    if (!channel.isDefined) continue;
    if (channel.gates[0] === gate || channel.gates[1] === gate) {
      return true;
    }
  }
  return false;
}

/**
 * Drie-staat classificatie van een poort in de huidige chart:
 *  - `inDefinedChannel`: poort behoort tot een defined kanaal.
 *  - `inDefinedCenter`: poort niet in defined kanaal, maar het center is defined.
 *  - `inUndefinedCenter`: poort zit in een undefined center.
 *
 * Wordt gebruikt door het gate-info panel om de juiste planeet-invloed-keynote
 * inline te tonen.
 */
export function resolveHdGateDefinitionState(
  gate: number,
  renderState: HdBodygraphRenderState,
): 'inDefinedChannel' | 'inDefinedCenter' | 'inUndefinedCenter' {
  if (isGateInDefinedChannel(gate, renderState)) {
    return 'inDefinedChannel';
  }
  if (isGateInDefinedCenter(gate, renderState)) {
    return 'inDefinedCenter';
  }
  return 'inUndefinedCenter';
}

/**
 * Definitie-staat van een poort in de VOLLEDIGE transit-chart (natal + transit):
 * dezelfde combinatie die de bodygraph zelf met een transitOverlay tekent
 * (`configuration + overlay-activaties` door de render-state factory).
 *
 * Gebruikt voor de planeet-invloed-variant van TRANSIT-activaties (just-now):
 * een transit die een kanaal/center voltooit moet de 'defined'-variant tonen,
 * ook wanneer de poort natal in een undefined center ligt.
 *
 * Retourneert `undefined` wanneer er geen transit-activaties zijn — de consumer
 * valt dan terug op de natal {@link resolveHdGateDefinitionState}.
 */
export function resolveHdTransitGateDefinitionState(
  gate: number,
  baseConfiguration: HdBodygraphConfig,
  transitActivations: readonly HdBodygraphPlanetaryActivation[],
): 'inDefinedChannel' | 'inDefinedCenter' | 'inUndefinedCenter' | undefined {
  if (transitActivations.length === 0) {
    return undefined;
  }
  const combinedRenderState = new HdBodygraphRenderStateFactory().create({
    ...baseConfiguration,
    activations: [...baseConfiguration.activations, ...transitActivations],
  });
  return resolveHdGateDefinitionState(gate, combinedRenderState);
}

/**
 * Find a planetary activation by side and planet id from a preset.
 */
export function findActivation(
  preset: HdBodygraphChartPreset,
  side: HdBodygraphSide,
  planetId: number,
): HdBodygraphPlanetaryActivation | undefined {
  return findActivationFromList(preset.planetaryActivations, side, planetId);
}

/**
 * Find a planetary activation by side and planet id from an activation array.
 */
export function findActivationFromList(
  activations: readonly HdBodygraphPlanetaryActivation[],
  side: HdBodygraphSide,
  planetId: number,
): HdBodygraphPlanetaryActivation | undefined {
  return activations.find(
    activation => activation.side === side && activation.planetId === planetId,
  );
}

/** Visual state of the 4 quadrants in the base badge. */
export interface HdBaseBadgeState {
  readonly tl: boolean; // top-left quadrant filled
  readonly tr: boolean; // top-right quadrant filled
  readonly bl: boolean; // bottom-left quadrant filled
  readonly br: boolean; // bottom-right quadrant filled
}

/**
 * Resolves the base badge quadrant state based on base value, planet key and side.
 * See docs/human-design/base-logic.md for the full specification.
 */
export function resolveBaseBadgeState(
  base: number,
  key: HdPlanetKey,
  side: HdBodygraphSide,
): HdBaseBadgeState {
  const isSunOrEarth = key === 'sun' || key === 'earth';
  const isNorthNode = key === 'northNode';
  const isSouthNode = key === 'southNode';
  const isDesign = side === 'design';

  switch (base) {
    case 1: {
      const tl = (isSunOrEarth && isDesign) || isSouthNode;
      const bl = (isSunOrEarth && !isDesign) || isNorthNode;
      return { tl, tr: false, bl, br: false };
    }
    case 2:
      return { tl: false, tr: true, bl: false, br: false };
    case 3: {
      const isPartial = isNorthNode || (isSunOrEarth && !isDesign);
      if (isPartial) {
        return { tl: true, tr: false, bl: false, br: false };
      }
      return { tl: true, tr: true, bl: true, br: true };
    }
    case 4:
      return { tl: false, tr: false, bl: false, br: true };
    case 5: {
      const isPartial = (isSunOrEarth && isDesign) || isSouthNode;
      if (isPartial) {
        return { tl: false, tr: false, bl: true, br: false };
      }
      return { tl: true, tr: true, bl: true, br: true };
    }
    default:
      return { tl: false, tr: false, bl: false, br: false };
  }
}

/**
 * Rotates a base badge state 90° counter-clockwise (the "LPS rotation"), keeping
 * the underlying logical relationships between filled quadrants intact but
 * shifting their visual positions to align with the Lunar Planetary Square layout:
 *   TL → BL, BL → BR, BR → TR, TR → TL.
 *
 * The base number itself is rendered separately and stays upright; only the
 * quadrant background flags are remapped here.
 */
export function rotateBaseBadgeStateForLps(state: HdBaseBadgeState): HdBaseBadgeState {
  return {
    tl: state.tr,
    tr: state.br,
    br: state.bl,
    bl: state.tl,
  };
}

/**
 * Role of a Node activation with respect to the Magnetic Monopole's base orientation:
 *  - `prime`: anchors the internal essence to the outer world via its own side's base
 *    orientation. Applies to Personality North Node and Design South Node.
 *  - `link`: crossover that "glues" Personality and Design by adopting the other side's
 *    base orientation. Applies to Personality South Node and Design North Node.
 *  - `null`: not a Node activation (or planet that has no node-role).
 */
export type HdNodeRole = 'prime' | 'link' | null;

/**
 * Determines whether a planet activation is a Prime Node, a Link Node, or neither.
 * Only North/South Node planet keys can produce a non-null role.
 */
export function resolveHdNodeRole(key: HdPlanetKey, side: HdBodygraphSide): HdNodeRole {
  if (key === 'northNode') {
    return side === 'personality' ? 'prime' : 'link';
  }
  if (key === 'southNode') {
    return side === 'design' ? 'prime' : 'link';
  }
  return null;
}

/**
 * The "mat" / "diagonal" used to resolve the Base orientation of a planet
 * activation. Determines:
 *  - which base→quadrant mapping is used (`resolveBaseBadgeState`);
 *  - which side of the chart the Lunar Planetary Square block lights up on
 *    when hovering the base badge (`resolveBaseHoverTarget`).
 *
 * Sun/Earth follow their own side's mat. North Nodes always use the
 * Personality mat (Prime on personality, Link on design). South Nodes always
 * use the Design mat (Prime on design, Link on personality).
 *
 * See `.\docs\human-design\base-logic.md`.
 */
export type HdBaseMat = 'personality' | 'design';

/**
 * Determines which mat (Personality vs Design) is used for the Base
 * orientation of the given planet/side combination. Returns `null` for
 * planets without a base badge (only Sun, Earth, North Node, South Node
 * have one).
 */
export function resolveBaseMat(key: HdPlanetKey, side: HdBodygraphSide): HdBaseMat | null {
  if (key === 'sun' || key === 'earth') {
    if (side === 'personality' || side === 'design') return side;
    return null;
  }
  if (key === 'northNode') return 'personality';
  if (key === 'southNode') return 'design';
  return null;
}

/**
 * Maps base value (1–5) to the corresponding LPS planet key for each mat.
 * See `.\docs\human-design\base-logic.md` (§ Base → LPS Diagonal Mapping).
 *
 * Design Mat (Prana Diagonal):
 *   1 → Pluto, 2 → Uranus, 3 → Mars (center), 4 → Moon, 5 → Saturn.
 * Personality Mat (Martian Diagonal):
 *   1 → Pluto, 2 → Moon, 3 → Saturn, 4 → Uranus, 5 → Mars (center).
 */
const BASE_TO_LPS_PLANET: Readonly<Record<HdBaseMat, Readonly<Partial<Record<number, HdPlanetKey>>>>> = {
  design: {
    1: 'pluto',
    2: 'uranus',
    3: 'mars',
    4: 'moon',
    5: 'saturn',
  },
  personality: {
    1: 'pluto',
    2: 'moon',
    3: 'saturn',
    4: 'uranus',
    5: 'mars',
  },
};

/**
 * Resolves the LPS planet block that "corresponds" to a base badge —
 * i.e. the block that should light up when hovering that base indicator.
 *
 * The mat (determined by planet key + side) drives both:
 *  - which base→planet mapping is used;
 *  - which side of the LPS grid (`personality` vs `design`) is highlighted.
 *
 * Link-node crossover is implicit in {@link resolveBaseMat}: hovering the
 * base of P-South Node (uses Design mat) highlights a block on the design
 * side; hovering D-North Node (uses Personality mat) highlights a block on
 * the personality side. Sun/Earth and Prime Nodes always stay on their own
 * side.
 *
 * Returns `null` for planets without a base badge or for invalid base
 * values (only 1–5 are valid).
 */
export function resolveBaseHoverTarget(
  base: number,
  key: HdPlanetKey,
  side: HdBodygraphSide,
): { readonly planetKey: HdPlanetKey; readonly side: HdBodygraphSide } | null {
  const mat = resolveBaseMat(key, side);
  if (mat == null) return null;
  const targetPlanet = BASE_TO_LPS_PLANET[mat][base];
  if (targetPlanet == null) return null;
  return { planetKey: targetPlanet, side: mat };
}

/**
 * Resolves the fixing state from the Maia Mechanics fixing data.
 * The fixing data can be either an object with `state` property or a plain number.
 */
export function resolveHdFixingState(activation: HdBodygraphPlanetaryActivation): HdFixingState {
  const metadata = activation.metadata;

  if (!metadata) {
    return 'none';
  }

  const fixing = metadata['fixing'];

  if (fixing == null) {
    return 'none';
  }

  const fixingState = typeof fixing === 'number' ? fixing : (fixing as { state?: number }).state;

  switch (fixingState) {
    case 1: return 'detriment';
    case 2: return 'exaltation';
    case 3: return 'both';
    default: return 'none';
  }
}
